import {useEffect} from 'react';
import {useQueryClient} from '@tanstack/react-query';
import {allowsAppleBilling} from '../../lib/billing/billingChannel';
import {isAppleIapEnabled, acceptAppleTransaction} from '../../lib/billing/appleSubscriptionService';
import {addAppleTransactionListener} from '../../lib/billing/appleStoreKit';
import {supabase} from '../../lib/supabase/client';

function AppleTransactionBridge({session}) {
  const queryClient = useQueryClient();

  useEffect(() => {
    const enabled = isAppleIapEnabled();
    if (!session?.user?.id || !supabase || !allowsAppleBilling(undefined, enabled)) return undefined;

    let active = true;
    let listener;
    Promise.resolve(addAppleTransactionListener(async ({transaction}) => {
      if (!active) return;
      try {
        await acceptAppleTransaction(transaction, {client: supabase});
        if (!active) return;
        await Promise.all([
          queryClient.invalidateQueries({queryKey: ['apple-purchase-eligibility', session.user.id]}),
          queryClient.invalidateQueries({queryKey: ['apple-subscription-status', session.user.id]}),
          queryClient.invalidateQueries({queryKey: ['subscription', session.user.id]}),
          queryClient.invalidateQueries({queryKey: ['business-feature-plan', session.user.id]})
        ]);
      } catch {
        // Leave the verified transaction unfinished. StoreKit or Restore can
        // safely retry after the server verifier becomes available again.
      }
    })).then((handle) => {
      if (!active) handle.remove();
      else listener = handle;
    }).catch(() => {
      // The feature/platform gate normally prevents registration failures.
      // Remain fail-closed if native listener registration still fails.
    });

    return () => {
      active = false;
      if (listener) listener.remove();
    };
  }, [queryClient, session?.user?.id]);

  return null;
}

export default AppleTransactionBridge;
